/**
 * Environment Configuration
 * Fetches environment config from backend API
 * Determines whether app runs in dev mode (Sepolia) or prod mode (BSC)
 */

export interface EnvironmentConfig {
  isDevMode: boolean;
  environment: 'development' | 'production';
  contracts: {
    swap: {
      oldToken: string;
      newToken: string;
      swapContract: string;
      chainId: number;
      chainName: string;
    };
    staking: {
      farToken: string;
      stakingContract: string;
      chainId: number;
      chainName: string;
    };
  };
  networks: {
    production: {
      chainId: number;
      name: string;
      rpcUrl: string;
    };
    development: {
      chainId: number;
      name: string;
      rpcUrl: string;
      note?: string;
    };
  };
}

// Backend API URL
// Use relative URL in production (same domain), localhost in development
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || (location.hostname === 'localhost' ? 'http://localhost:3001' : '');

// Cache for config
let cachedConfig: EnvironmentConfig | null = null;
let configPromise: Promise<EnvironmentConfig> | null = null;

/**
 * Fetch environment configuration from backend
 * Cached after first fetch
 */
export async function fetchEnvironmentConfig(): Promise<EnvironmentConfig> {
  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig;
  }

  // Return in-flight promise if already fetching
  if (configPromise) {
    return configPromise;
  }

  // Fetch from backend
  configPromise = fetch(`${BACKEND_URL}/api/config`)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.statusText}`);
      }
      const data = await response.json();
      if (!data.success || !data.config) {
        throw new Error('Invalid config response');
      }
      cachedConfig = data.config;
      return cachedConfig as EnvironmentConfig;
    })
    .catch((error) => {
      console.error('Failed to fetch environment config:', error);
      // Fallback to production config
      const fallbackConfig: EnvironmentConfig = {
        isDevMode: false,
        environment: 'production',
        contracts: {
          swap: {
            oldToken: '0xDC816B6bd4786720e91Eb17D4DaBf0E1eEf5eE91',
            newToken: '0xC44F9f08E524669E5DEeEBC9EB142c81edfAd178',
            swapContract: '0xBA72ccedd7b94CDFCAE8107F0EB23a1b88A35756',
            chainId: 56,
            chainName: 'BSC Mainnet',
          },
          staking: {
            farToken: '0xC44F9f08E524669E5DEeEBC9EB142c81edfAd178',
            stakingContract: '0x9f902fac7655985eB0961b3A818aa7D597682Cdc',
            chainId: 56,
            chainName: 'BSC Mainnet',
          },
        },
        networks: {
          production: {
            chainId: 56,
            name: 'BSC Mainnet',
            rpcUrl: 'https://bsc-dataseed.binance.org/',
          },
          development: {
            chainId: 11155111,
            name: 'BSC Mainnet',
            rpcUrl: 'https://rpc.sepolia.org',
          },
        },
      };
      cachedConfig = fallbackConfig;
      return fallbackConfig;
    })
    .finally(() => {
      configPromise = null;
    });

  return configPromise;
}

/**
 * Check if app is in development mode (Sepolia testnet)
 */
export async function isDevMode(): Promise<boolean> {
  const config = await fetchEnvironmentConfig();
  return config.isDevMode;
}

/**
 * Check if app is in production mode (BSC mainnet)
 */
export async function isProdMode(): Promise<boolean> {
  const config = await fetchEnvironmentConfig();
  return !config.isDevMode;
}

/**
 * Get current network config based on environment
 */
export async function getCurrentNetwork() {
  const config = await fetchEnvironmentConfig();
  return config.isDevMode ? config.networks.development : config.networks.production;
}

/**
 * Get swap contract config based on environment
 */
export async function getSwapContract() {
  const config = await fetchEnvironmentConfig();
  return config.contracts.swap;
}

/**
 * Get staking contract config based on environment
 */
export async function getStakingContract() {
  const config = await fetchEnvironmentConfig();
  return config.contracts.staking;
}

/**
 * Clear cached config (useful for testing or forcing refresh)
 */
export function clearConfigCache() {
  cachedConfig = null;
  configPromise = null;
}
